import { Injectable } from '@angular/core';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import {
    AchievementModuleDataObj, AgentCounterModuleDataObj, CurrentAgentStatesModuleDataObj, GaugeModuleDataApiModel, RacingModuleDataObj, SummaryModuleDataApiModel, ThermometerModuleDataApiModel, TopModuleDataObj,
    WVBase, WVCustom, WVData, WVMain, WVRacing, WallboardInfoObj, spotlightDataObj
} from './wallboard-view.model';
import { WallboardViewChallengesComponent } from './wallboard-view-challenges/wallboard-view-challenges.component';
import { WallboardViewCurrentAgentStatesComponent } from './wallboard-view-current-agent-states/wallboard-view-current-agent-states.component';
import { WallboardViewCustomComponent } from './wallboard-view-custom/wallboard-view-custom.component';
import { WallboardViewGamesComponent } from './wallboard-view-games/wallboard-view-games.component';
import { WallboardViewImageComponent } from './wallboard-view-image/wallboard-view-image.component';
import { WallboardViewLeaderboardComponent } from './wallboard-view-leaderboard/wallboard-view-leaderboard.component';
import { WallboardViewMarketplaceComponent } from './wallboard-view-marketplace/wallboard-view-marketplace.component';
import { WallboardViewRacingComponent } from './wallboard-view-racing/wallboard-view-racing.component';
import { WallboardViewScorecardComponent } from './wallboard-view-scorecard/wallboard-view-scorecard.component';
import { WallboardViewSpotlightComponent } from './wallboard-view-spotlight/wallboard-view-spotlight.component';
import { WallboardViewSummaryComponent } from './wallboard-view-summary/wallboard-view-summary.component';
import { WallboardViewVideoComponent } from './wallboard-view-video/wallboard-view-video.component';
import { WallboardsService } from '../wallboards.service';
import { CurrentChallengeWSObj, ModuleListWrapperObj, ModuleType } from '../wallboards.model';

import { WallboardViewCurrentAgentStatesResponsiveComponent } from './wallboard-view-current-agent-states-responsive/wallboard-view-current-agent-states-responsive.component';
@Injectable({
    providedIn: 'root'
})
export class WallboardViewService {
    public static readonly REALTIME_DATA_REFRESH_INTERVAL = 5000;
    public static readonly HISTORICAL_DATA_REFRESH_INTERVAL = 90000;
    public static readonly ERROR_RETRY_INTERVAL = 15000;

    // In-memory cache for the most recent successful Current Agent States response.
    // Lives in this service singleton for the whole page session, so a freshly-
    // created CAS instance (after slide rotation destroys and re-creates the
    // component) can seed its initial render with the previous instance's last
    // known values, instead of flashing empty while its own first API call is
    // in flight. No TTL is needed because every successful 5s refresh rewrites
    // the entry; the cache is automatically cleared on page reload / tab close.
    private readonly casMemoryCache = new Map<string, CurrentAgentStatesModuleDataObj>();

    public WVMain: WVMain;
    public WVCustom: WVCustom;
    public WVLeaderboard: WVBase;
    public WVRacing: WVRacing;
    public WVScorecard: WVData;
    public WVSummary: WVBase;
    public WVImage: WVData;
    public WVAgentStatesResponsive: WVBase;
    public WVVideo: WVData;
    public WVSpotlight: WVData;
    public WVAgentStates: WVBase;
    public WVGames: WVData;
    public WVChallenges: WVCustom;
    public WVMarketplace: WVBase;

    constructor(
        private cvApiService: CVApiService,
        public wallboardsService: WallboardsService,
    ) {
        this.WVMain = new WVMain();
        this.WVCustom = new WVCustom();
        this.WVLeaderboard = new WVBase();
        this.WVRacing = new WVRacing();
        this.WVScorecard = new WVData();
        this.WVSummary = new WVCustom();
        this.WVImage = new WVData();
        this.WVAgentStatesResponsive = new WVCustom();
        this.WVVideo = new WVData();
        this.WVSpotlight = new WVData();
        this.WVAgentStates = new WVCustom();
        this.WVGames = new WVData();
        this.WVChallenges = new WVCustom();
        this.WVMarketplace = new WVCustom();
    }

    public async loadWallboardInfo(wallboardId: number, slideIndex: number): Promise<void> {
        this.WVMain.Slides = await this.wallboardsService.loadWallboardSlides(wallboardId, slideIndex);

        if (this.WVMain.Slides.length > 0)
            this.WVMain.CurrentSlide = 0;
        if (this.WVMain.Slides.length > 1)
            this.WVMain.NextSlide = 1;
        if (this.WVMain.Slides.length === 1)
            this.WVMain.NextSlide = 0;
    }

    async loadWallboardInfoForView(): Promise<WallboardInfoObj> {
        let response = await this.cvApiService.get<WallboardInfoObj>(`/api/v1/wallboardInfoForView`);
        if (response.status_code >= 200 && response.status_code < 300) {
            if (response.data?.Active === false) {
                throw new Error('WALLBOARD_INACTIVE');
            }
            return response.data;
        }
        throw response;
    }

    public async LoadWallboardInfoForView(): Promise<void> {
        const response = await this.loadWallboardInfoForView();

        this.WVMain.WallboardCreatorID = response.WallboardCreatorID;
        this.WVMain.Slides = response.WallboardSlides;
        if (response.WallboardSlides.length > 0)
            this.WVMain.CurrentSlide = 0;
        if (response.WallboardSlides.length > 1)
            this.WVMain.NextSlide = 1;
        if (response.WallboardSlides.length === 1)
            this.WVMain.NextSlide = 0;
    }

    async logWallboardRun(wallboardId: number): Promise<boolean> {
        let response = await this.cvApiService.get<boolean>(`/api/v1/logWallboardRun/${wallboardId}`);
        if (response.status_code >= 200 && response.status_code < 300)
            return response.data;
        throw response;
    }

    async loadModuleState(slideType: ModuleType, creatorId: number, moduleKey: string): Promise<ModuleListWrapperObj> {
        try {
            let response = await this.cvApiService.post<ModuleListWrapperObj>(`/api/v1/moduleState/${slideType}/${creatorId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load module state.`);
            // Some modules have countermeasures for failed loads, so this is helpful
            throw error;
        }
    }

    async getMetricName(wallboardId: number, moduleKey: string): Promise<string> {
        try {
            let response = await this.cvApiService.post<string>(`/api/v1/metricName/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load leaderboard data.`);
        }
    }

    async getLeaderboardModuleData(wallboardId: number, showTop: number, moduleKey: string): Promise<TopModuleDataObj[]> {
        try {
            let response = await this.cvApiService.post<TopModuleDataObj[]>(`/api/v1/leaderboardData/${wallboardId}/${showTop}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load leaderboard data.`);
        }
    }

    async getRacingModuleData(wallboardId: number, moduleKey: string): Promise<RacingModuleDataObj[]> {
        try {
            let response = await this.cvApiService.post<RacingModuleDataObj[]>(`/api/v1/racingModuleData/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load racing module data.`);
        }
	}
    async getCurrentAgentStatesResponsiveModuleState(moduleKey, creatorId) {
        try {
            const response = await this.cvApiService.post<any>("/api/v1/moduleState/22/" + creatorId, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        }
        catch (err) {
            console.error("Failed getting state for Responsive Current Agent States Slide");
            throw err;
        }
    }
    async getCurrentAgentStatesResponsiveModuleData(wallboardId: number, moduleKey: string) {
        try {
            let url = `/api/v1/currentAgentStatesResponsiveData/${wallboardId}/${moduleKey}`;
            const response = await this.cvApiService.get<any>(url);
            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        }
        catch (err) {
            console.error("Failed getting data for Responsive Current Agent States Slide");
            throw err;
        }
    }

    async getImageState(moduleKey, creatorId) {
        return this.cvApiService.post<any>("/api/v1/moduleState/13/" + creatorId, JSON.stringify(moduleKey));
    }
    async getImageAspect(imgId) {
        return this.cvApiService.get<any>("/api/v1/imageAspect/" + imgId);
    }

    async getSummaryModuleData(wallboardId: number, showTop: number, hasTotalsRow: boolean, hasPagination: boolean, moduleKey: string): Promise<SummaryModuleDataApiModel> {
        try {
            let response = await this.cvApiService.post<SummaryModuleDataApiModel>(`/api/v1/summaryModuleData/${wallboardId}/${showTop}/${hasTotalsRow}/${hasPagination}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load summary module data.`);
        }
    }

    async getSummaryMetricNames(wallboardId: number, moduleKey: string): Promise<string[]> {
        try {
            let response = await this.cvApiService.post<string[]>(`/api/v1/summaryMetricNames/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load summary metric names.`);
        }
    }

    async getSpotlightModuleData(creatorId: number, loginId: number, showBadges: boolean, badgeCount: number, moduleKey: string): Promise<spotlightDataObj> {
        try {
            let response = await this.cvApiService.post<spotlightDataObj>(`/api/v1/spotlightModuleData/${creatorId}/${loginId}/${showBadges}/${badgeCount}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load spotlight module data.`);
            // Some modules have countermeasures for failed loads, so this is helpful
            throw error;
        }
    }

    async getGaugeModuleData(wallboardId: number, moduleKey: string): Promise<GaugeModuleDataApiModel> {
        try {
            let response = await this.cvApiService.post<GaugeModuleDataApiModel>(`/api/v1/gaugeModuleData/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load gauge module data.`);
            // Some modules have countermeasures for failed loads, so this is helpful
            throw error;
        }
    }

    async getThermometerKpiModuleData(wallboardId: number, moduleKey: string): Promise<ThermometerModuleDataApiModel> {
        // The thermometer method gets the data in the same way that the KPI module needs
        try {
            let response = await this.cvApiService.post<ThermometerModuleDataApiModel>(`/api/v1/thermometerModuleData/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load thermometer/KPI module data.`);
            // Some modules have countermeasures for failed loads, so this is helpful
            throw error;
        }
    }

    async getAchievementModuleData(wallboardId: number, moduleKey: string): Promise<AchievementModuleDataObj[]> {
        try {
            let response = await this.cvApiService.post<AchievementModuleDataObj[]>(`/api/v1/achievementModuleData/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load achievement module data.`);
        }
    }

    async getTopModuleData(wallboardId: number, moduleKey: string): Promise<TopModuleDataObj[]> {
        try {
            let response = await this.cvApiService.post<TopModuleDataObj[]>(`/api/v1/topModuleData/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load top module data.`);
        }
    }

    async getAgentCounterModuleData(wallboardId: number, moduleKey: string): Promise<AgentCounterModuleDataObj> {
        try {
            let response = await this.cvApiService.post<AgentCounterModuleDataObj>(`/api/v1/agentCounterModuleData/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300)
                return response.data;
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load agent counter module data.`);
        }
    }

    async getCurrentAgentStatesModuleData(wallboardId: number, moduleKey: string): Promise<CurrentAgentStatesModuleDataObj> {
        try {
            let response = await this.cvApiService.post<CurrentAgentStatesModuleDataObj>(`/api/v1/currentAgentStatesData/${wallboardId}`, JSON.stringify(moduleKey));
            if (response.status_code >= 200 && response.status_code < 300) {
                this.casMemoryCache.set(this.casCacheKey(wallboardId, moduleKey), response.data);
                return response.data;
            }
            else throw response;
        }
        catch (error) {
            console.error(`Failed to load current agent states module data.`);
        }
    }

    /**
     * Returns the most recently cached Current Agent States response for this
     * wallboard + module, or null if no entry exists. Used by the CAS component
     * to seed its initial render after a slide-rotation destroy/recreate cycle
     * so badges don't flash empty while the new instance's first API call is
     * in flight.
     */
    public readCachedAgentStatesData(wallboardId: number, moduleKey: string): CurrentAgentStatesModuleDataObj | null {
        return this.casMemoryCache.get(this.casCacheKey(wallboardId, moduleKey)) ?? null;
    }

    private casCacheKey(wallboardId: number, moduleKey: string): string {
        return `${wallboardId}:${moduleKey}`;
    }

    async getChallengesLoginsData(wallboardId: number, moduleKey: string): Promise<CurrentChallengeWSObj[]> {
        let challengesLogins = this.wallboardsService.loadChallengesLogins(wallboardId, moduleKey);
        let runningChallenges = this.wallboardsService.loadRunningChallenges();

        return Promise.all([challengesLogins, runningChallenges]).then(responses => {
            let groups = responses[0];
            let activeChallenges = responses[1].RunningChallenges;

            let groupID: any = 0;
            const filteredChallenges: CurrentChallengeWSObj[] = [];

            for (let i = 0; i < activeChallenges.length; i++) {
                if (!filteredChallenges.includes(activeChallenges[i])) {
                    for (let j = 0; j < activeChallenges[i].Rankings.length; j++) {
                        groupID = groups.filter((g) => g === activeChallenges[i].Rankings[j].GroupID);

                        if (groupID != 0) {
                            filteredChallenges.push(activeChallenges[i]);
                            break;
                        }
                    }
                }
            }

            return filteredChallenges;
        });
    }

    calculatePagination(calculatedInterval: number, slideIntervalTime: number, totalPages: number, verifyNextSlide: boolean): number[] {
        if (calculatedInterval < 3000) {
            calculatedInterval = 3000;
            slideIntervalTime = (calculatedInterval * totalPages);
        }

        let slideIndex = 0;

        if (verifyNextSlide) {
            slideIndex = this.WVMain.NextSlide;
        } else if (this.WVMain.LastSlide) {
            slideIndex = this.WVMain.Slides.length - 1;
        } else {
            slideIndex = this.WVMain.CurrentSlide;
        }

        this.WVMain.Slides[slideIndex].SlideInterval = slideIntervalTime / 1000;

        return [slideIntervalTime, calculatedInterval];
    }

    runPagination(index: number, currentPage: number): number {
        if (index === 0)
            currentPage = 0;
        else
            currentPage++;

        return currentPage;
    }

    rotateSlides(): void {
        this.WVMain.CurrentSlide = this.WVMain.NextSlide;

        if (this.WVMain.Slides.length - 1 > this.WVMain.NextSlide)
            this.WVMain.NextSlide++;
        else
            this.WVMain.NextSlide = 0;
    }

    skipSlide(): void {
        if (this.WVMain.Slides.length - 1 > this.WVMain.NextSlide)
            this.WVMain.NextSlide++;
        else
            this.WVMain.NextSlide = 0;
    }

    /**
     * Loads the specified slide and returns the correct component.
     * @param next True if the next slide should be loaded, false if the current slide should be. Defaults to false.
     */
    loadSlide(next: boolean = false): any {
        let slideIndex = next ? this.WVMain.NextSlide : this.WVMain.CurrentSlide;
        let slide = this.WVMain.Slides[slideIndex];

        if (slideIndex === 0)
            this.logWallboardRun(slide.WallboardID);

        if (slide.WallboardModuleDetails.length === 0) {
            this.WVCustom.Slide = slide;
            return WallboardViewCustomComponent;
        }

        switch (slide.WallboardModuleDetails[0].WallboardModuleID) {
            case ModuleType.Leaderboard:
                this.WVLeaderboard.Slide = slide;
                return WallboardViewLeaderboardComponent;
            case ModuleType.Racing:
                this.WVRacing.Slide = slide;
                return WallboardViewRacingComponent;
            case ModuleType.Scorecard:
                this.WVScorecard.Slide = slide;
                return WallboardViewScorecardComponent;
            case ModuleType.Summary:
                this.WVSummary.Slide = slide;
                return WallboardViewSummaryComponent;
            case ModuleType.Image:
                this.WVImage.Slide = slide;
                return WallboardViewImageComponent;
            case ModuleType.Video:
                this.WVVideo.Slide = slide;
                return WallboardViewVideoComponent;
            case ModuleType.Spotlight:
                this.WVSpotlight.Slide = slide;
                return WallboardViewSpotlightComponent;
            case ModuleType.Gauge:
            case ModuleType.Thermometer:
            case ModuleType.SummaryModule:
            case ModuleType.Achievement:
            case ModuleType.Text:
            case ModuleType.ImageModule:
            case ModuleType.Top:
            case ModuleType.Kpi:
            case ModuleType.AgentCounter:
                this.WVCustom.Slide = slide;
                return WallboardViewCustomComponent;
            case ModuleType.CurrentAgentStates:
                this.WVAgentStates.Slide = slide;
                return WallboardViewCurrentAgentStatesComponent;
            case ModuleType.Games:
                this.WVGames.Slide = slide;
                return WallboardViewGamesComponent;
            case ModuleType.Challenges:
                this.WVChallenges.Slide = slide;
                return WallboardViewChallengesComponent;
            case ModuleType.Marketplace:
                this.WVMarketplace.Slide = slide;
                return WallboardViewMarketplaceComponent;
            case ModuleType.ResponsiveCurrentAgentStates:
                this.WVAgentStatesResponsive.Slide = slide;
                return WallboardViewCurrentAgentStatesResponsiveComponent;
        }
    }

    loadNextSlideData(): void {
        this.WVMain.NextSlideReady = false;
    }

    formatMetricValue(metricValue, metricNumericType: number, metricTimeType: number): string {
        if (metricNumericType === undefined)
            metricNumericType = 1; // Default to NumericType: Numeric

        if (metricTimeType === undefined)
            metricTimeType = 0; // Default to TimeType: None

        // format value using js Number locale functions
        var metricNumber = Number(metricValue);
        let CVCultureName = 'en-US'
        switch (metricNumericType) {
            case 0: // Currency
                return metricNumber.toLocaleString(CVCultureName, {
                    style: "currency",
                    currency: CVISOCurrencySymbol,
                    maximumFractionDigits: 2,
                });
            case 1: // Number
                return metricNumber.toLocaleString(CVCultureName, {
                    maximumFractionDigits: 2,
                });
            case 2: // Percent
                return metricNumber.toLocaleString(CVCultureName, {
                    style: "percent",
                    maximumFractionDigits: 2,
                });
            case 3: // Time
                switch (metricTimeType) {
                    case 4: // Time
                        let hours = Math.floor(metricValue / 3600);
                        let minutes = Math.floor((metricValue - (hours * 3600)) / 60);
                        let seconds = Math.floor(metricValue - (hours * 3600) - (minutes * 60));

                        // Zero padding
                        let hoursStr = '' + hours;
                        let minutesStr = '' + minutes;
                        let secondsStr = '' + seconds;

                        if (hours < 10)
                            hoursStr = "0" + hours;
                        else // No zero-pad, but format as a number in case the number is large enough to have seperators
                            hoursStr = Number(hours).toLocaleString(CVCultureName);

                        if (minutes < 10)
                            minutesStr = "0" + minutes;
                        if (seconds < 10)
                            secondsStr = "0" + seconds;

                        // hh?:mm:ss
                        let formattedStr = minutesStr + ':' + secondsStr
                        //if (hours !== 0) {
                        formattedStr = hoursStr + ':' + formattedStr;
                        //}

                        return formattedStr;
                    default: // 0 (none) or 1 (second) or 2 (minute) or 3 (hour)
                        return metricNumber.toLocaleString(CVCultureName, {
                            maximumFractionDigits: 2,
                        });
                }
            default: // Value not in enum, just format as number
                return metricNumber.toLocaleString(CVCultureName, { maximumFractionDigits: 2, });
        }
    }
}
